import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, Card, Typography, Form, Input, Select, Switch, Button, Space, Row, Col, Tabs, message, Modal } from 'antd';

const { Title } = Typography;

const WhiteLabelAgencyDetails = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const basics = Form.useWatch('basics', form);
  const currentStatus = basics?.status;

  // Placeholder: mimic load by id
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      form.setFieldsValue({
        basics: {
          name: 'BrightBrand Labs',
          domain: 'brightbrand.io',
          email: 'owner@brightbrand.io',
          status: 'active',
        },
        branding: {
          brandName: 'BrightBrand',
          primaryColor: '#2A76E8',
          supportEmail: 'support@brightbrand.io',
        },
        billing: {
          plan: 'enterprise',
          invoiceEmail: 'billing@brightbrand.io',
          autoCharge: true,
        },
        roles: {
          defaultRole: 'admin',
        },
      });
      setLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [form, id]);

  const handleSave = async () => {
    try {
      await form.validateFields();
      setSaving(true);
      // TODO: Call backend to save all sections for agency id
      setTimeout(() => {
        setSaving(false);
        message.success('Agency details updated');
      }, 500);
    } catch (e) {
      // validation errors
    }
  };

  const toggleStatus = () => {
    const toStatus = currentStatus === 'active' ? 'disabled' : 'active';
    Modal.confirm({
      title: `${currentStatus === 'active' ? 'Deactivate' : 'Activate'} Agency`,
      content: `Are you sure you want to set status to "${toStatus}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const current = form.getFieldValue('basics') || {};
        form.setFieldsValue({ basics: { ...current, status: toStatus } });
        message.success(`Status updated to ${toStatus}`);
      }
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Admin' }, { title: 'Agencies' }, { title: 'White-Label Agencies' }, { title: `#${id}` }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>White-Label Agency Details</Title>
        <Space>
          <Button onClick={toggleStatus} danger={currentStatus === 'active'}>
            {currentStatus === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
          <Button type="primary" loading={saving} onClick={handleSave}>Save Changes</Button>
        </Space>
      </div>

      <Card loading={loading}>
        <Tabs
          defaultActiveKey="basics"
          items={[
            {
              key: 'basics',
              label: 'Basics',
              children: (
                <Form form={form} layout="vertical" preserve={false}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={["basics", "name"]} label="Agency Name" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={["basics", "domain"]} label="Primary Domain" rules={[{ required: true }]}>
                        <Input addonBefore="https://" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={["basics", "email"]} label="Owner Email" rules={[{ type: 'email', required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={["basics", "status"]} label="Status" rules={[{ required: true }]}>
                        <Select options={[{ value: 'active' }, { value: 'pending' }, { value: 'disabled' }]} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              )
            },
            {
              key: 'branding',
              label: 'Branding',
              children: (
                <Form form={form} layout="vertical" preserve={false}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={["branding", "brandName"]} label="Brand Name" rules={[{ required: true }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={["branding", "primaryColor"]} label="Primary Color">
                        <Input type="color" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={["branding", "supportEmail"]} label="Support Email" rules={[{ type: 'email' }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              )
            },
            {
              key: 'billing',
              label: 'Billing',
              children: (
                <Form form={form} layout="vertical" preserve={false}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name={["billing", "plan"]} label="Plan" rules={[{ required: true }]}>
                        <Select options={[{ value: 'starter' }, { value: 'pro' }, { value: 'enterprise' }]} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={["billing", "invoiceEmail"]} label="Invoice Email" rules={[{ type: 'email' }]}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name={["billing", "autoCharge"]} label="Auto Charge" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'roles',
              label: 'Roles & Permissions',
              children: (
                <Form form={form} layout="vertical" preserve={false}>
                  <Form.Item name={["roles", "defaultRole"]} label="Default User Role">
                    <Select options={[{ value: 'viewer' }, { value: 'editor' }, { value: 'admin' }]} />
                  </Form.Item>
                </Form>
              )
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default WhiteLabelAgencyDetails;
